// Vertex-colour helpers for the Polyfork vehicles: one shared material, the paint lives in COLOR_0.

const lum = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

// A copy of `col` where every vertex of colour `from` becomes `target` (linear rgb). The models use a small flat
// palette, so the body paint is one exact colour; the tolerance only absorbs float noise.
export function repaint(col, from, target, tol = 0.02) {
  const out = new Float32Array(col);
  for (let i = 0; i < col.length; i += 3)
    if (
      Math.abs(col[i] - from[0]) <= tol &&
      Math.abs(col[i + 1] - from[1]) <= tol &&
      Math.abs(col[i + 2] - from[2]) <= tol
    )
      out.set(target, i);
  return out;
}

const area = (p, o) => {
  const ux = p[o + 3] - p[o],
    uy = p[o + 4] - p[o + 1],
    uz = p[o + 5] - p[o + 2],
    vx = p[o + 6] - p[o],
    vy = p[o + 7] - p[o + 1],
    vz = p[o + 8] - p[o + 2];
  return Math.hypot(uy * vz - uz * vy, uz * vx - ux * vz, ux * vy - uy * vx) / 2;
};

// Per triangle: 1 headlight, 2 tail light, 0 anything else. Non-indexed geometry; vehicles face +z.
// A lamp is a small patch of colour (under `maxShare` of the whole surface) at an end of the vehicle: bright at the
// front, red at the rear. The body paint covers far more, so a pale or red paint never glows. `skip` (one flag per
// triangle) leaves out parts such as the wheels.
export function lightRoles(pos, col, skip, end = 0.05, maxShare = 0.03) {
  const n = pos.length / 9,
    key = (o) =>
      ((col[o] * 63) | 0) * 4096 + ((col[o + 1] * 63) | 0) * 64 + ((col[o + 2] * 63) | 0),
    share = new Map();
  let z0 = Infinity,
    z1 = -Infinity,
    total = 0;
  for (let t = 0; t < n; t++) {
    const o = t * 9,
      a = area(pos, o);
    total += a;
    share.set(key(o), (share.get(key(o)) ?? 0) + a);
    for (let v = 2; v < 9; v += 3) {
      z0 = Math.min(z0, pos[o + v]);
      z1 = Math.max(z1, pos[o + v]);
    }
  }
  const band = (z1 - z0) * end,
    roles = new Uint8Array(n);
  for (let t = 0; t < n; t++) {
    const o = t * 9;
    if (skip?.[t] || share.get(key(o)) > total * maxShare) continue;
    const z = (pos[o + 2] + pos[o + 5] + pos[o + 8]) / 3,
      r = col[o],
      g = col[o + 1],
      b = col[o + 2];
    if (z > z1 - band && lum(r, g, b) > 0.55 && b < r * 1.1) roles[t] = 1;
    else if (z < z0 + band && r > 0.3 && r > 2.5 * g && r > 2.5 * b) roles[t] = 2;
  }
  return roles;
}
